import React, { useState } from "react";
import classnames from "classnames";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Collapse,
  Form,
} from "reactstrap";

// utils
import { DivideByKeyResultTypes } from "../utils";

// interfaces
import { ContactTypes, IContact } from "../types/contacts";

// components
import AppSimpleBar from "./AppSimpleBar";

// ✅ Estrutura de dados local
interface DataTypes {
  id: any;
  channelName: string;
  description: string;
}

interface ContactItemProps {
  contact: ContactTypes;
  selected: boolean;
  onSelectContact: (id: string | number, selected: boolean) => void;
}

// ✅ Item individual de contato
const ContactItem = ({
  contact,
  selected,
  onSelectContact,
}: ContactItemProps) => {
  const fullName = `${contact.firstName} ${contact.lastName ?? ""}`;
  const onCheck = (checked: boolean) => {
    onSelectContact(contact.id, checked);
  };

  return (
    <li>
      <div className="form-check">
        <Input
          type="checkbox"
          className="form-check-input"
          id={`contact-${contact.id}`}
          onChange={(e: any) => onCheck(e.target.checked)}
          checked={selected}
          value={fullName}
        />
        <Label className="form-check-label" htmlFor={`contact-${contact.id}`}>
          {fullName}
        </Label>
      </div>
    </li>
  );
};

interface CharacterItemProps {
  letterContacts: DivideByKeyResultTypes<IContact>;
  index: number;
  totalContacts: number;
  selectedContacts: Array<number | string>;
  onSelectContact: (id: string | number, selected: boolean) => void;
}

// ✅ Agrupamento de contatos por letra
const CharacterItem = ({
  letterContacts,
  index,
  totalContacts,
  selectedContacts,
  onSelectContact,
}: CharacterItemProps) => (
  <div>
    <div className="contact-list-title">{letterContacts.letter}</div>
    <ul
      className={classnames("list-unstyled", "contact-list", {
        "mb-0": index + 1 === totalContacts,
      })}
    >
      {(letterContacts.data || []).map((contact: any, key: number) => {
        const selected: boolean = selectedContacts.includes(contact.id);
        return (
          <ContactItem
            contact={contact}
            key={key}
            selected={selected}
            onSelectContact={onSelectContact}
          />
        );
      })}
    </ul>
  </div>
);

// interface AddGroupModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   onCreateChannel: (params: CreateChannelPostData) => void; // ⚠️ ainda não implementado
// }

/**
 * ⚙️ Modal de criação de grupo (em desenvolvimento)
 * — Comentado temporariamente os hooks e tipagens ausentes.
 */
const AddGroupModal = ({
  isOpen,
  onClose,
  onCreateChannel,
}: any /* 🔧 usando any temporariamente até tipagem CreateChannelPostData */) => {
  const [isOpenCollapse, setIsOpenCollapse] = useState(false);
  const toggleCollapse = () => setIsOpenCollapse(!isOpenCollapse);

  // const { categorizedContacts, totalContacts } = useContacts(); // ⚠️ desativado temporariamente
  const categorizedContacts: any[] = []; // placeholder
  const totalContacts = 0;

  const [selectedContacts, setSelectedContacts] = useState<
    Array<string | number>
  >([]);

  const onSelectContact = (id: string | number, selected: boolean) => {
    setSelectedContacts((prev) =>
      selected ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const [data, setData] = useState<DataTypes>({
    id: "",
    channelName: "",
    description: "",
  });

  const onDataChange = (
    field: "id" | "channelName" | "description",
    value: any
  ) => setData((prev) => ({ ...prev, [field]: value }));

  const onSubmit = () => {
    const params = {
      id: data.id,
      name: data.channelName,
      members: selectedContacts,
      description: data.description,
    };
    if (onCreateChannel) {
      onCreateChannel(params);
    } else {
      alert("Função de criação de canal ainda não implementada.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      tabIndex={-1}
      centered
      scrollable
      color="white"
      id="addgroup-exampleModal"
      role="dialog"
    >
      <ModalHeader toggle={onClose} className="bg-primary text-white">
        Criar grupo
      </ModalHeader>
      <ModalBody className="p-4">
        <Form>
          <div className="mb-4">
            <Label htmlFor="addgroupname-input" className="form-label">
              Nome do grupo
            </Label>
            <Input
              type="text"
              id="addgroupname-input"
              placeholder="Enter Group Name"
              value={data.channelName || ""}
              onChange={(e) => onDataChange("channelName", e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="form-label">Membros do grupo</label>
            <div className="mb-3">
              <Button
                color="light"
                size="sm"
                type="button"
                onClick={toggleCollapse}
              >
                Selecionar membros
              </Button>
            </div>

            <Collapse isOpen={isOpenCollapse} id="groupmembercollapse">
              <div className="card border">
                <div className="card-header">
                  <h5 className="font-size-15 mb-0">Contatos</h5>
                </div>
                <div className="card-body p-2">
                  <AppSimpleBar style={{ maxHeight: "150px" }}>
                    {(categorizedContacts || []).map(
                      (letterContacts: DivideByKeyResultTypes<IContact>, key: number) => (
                        <CharacterItem
                          letterContacts={letterContacts}
                          key={key}
                          index={key}
                          totalContacts={totalContacts}
                          selectedContacts={selectedContacts}
                          onSelectContact={onSelectContact}
                        />
                      )
                    )}
                  </AppSimpleBar>
                </div>
              </div>
            </Collapse>
          </div>

          <div className="mb-3">
            <Label htmlFor="addgroupdescription-input" className="form-label">
              Descrição
            </Label>
            <textarea
              className="form-control"
              id="addgroupdescription-input"
              rows={3}
              placeholder="Enter Description"
              value={data.description || ""}
              onChange={(e) => onDataChange("description", e.target.value)}
            />
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="link" type="button" onClick={onClose}>
          Fechar
        </Button>
        <Button type="button" color="primary" onClick={onSubmit}>
          Criar grupo
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddGroupModal;
